import type { Font } from 'opentype.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { compose, type Composition } from '@/core/pipeline';
import type { Project } from '@/core/project/types';
import { ensureCssFont, ensureOutlineFont } from '@/core/render/fontLoader';

/** Небольшая задержка предпросмотра: ввод не должен ждать пересчёта. */
export const PREVIEW_DEBOUNCE_MS = 120;

export function useDebounced<T>(value: T, delay = PREVIEW_DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export type FontState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; font: Font }
  | { status: 'error'; message: string };

/**
 * Загружает файл шрифта для выбранного начертания.
 * Результат устаревшей загрузки отбрасывается: подпись не должна внезапно
 * перерисоваться шрифтом, который пользователь уже переключил.
 */
export function useOutlineFont(fontId: string, weight: number, enabled: boolean): FontState {
  const [state, setState] = useState<FontState>({ status: 'idle' });

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle' });
      return;
    }
    let active = true;
    setState((prev) => (prev.status === 'ready' ? prev : { status: 'loading' }));
    void Promise.all([ensureOutlineFont(fontId, weight), ensureCssFont(fontId, weight).catch(() => undefined)])
      .then(([font]) => {
        if (active) setState({ status: 'ready', font });
      })
      .catch((err: unknown) => {
        if (active) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : 'Не удалось загрузить шрифт.',
          });
        }
      });
    return () => {
      active = false;
    };
  }, [fontId, weight, enabled]);

  return state;
}

/**
 * Предпросмотр. Тот же расчёт, что и при экспорте, только с задержкой:
 * набор текста не должен ждать пересборки макета.
 */
export function usePipeline(project: Project, font: Font | null): Composition {
  return useMemo(() => compose(project, font), [project, font]);
}

/** Тема интерфейса. Хранится отдельно от проекта и не влияет на экспорт. */
export type Theme = 'light' | 'dark';

const THEME_KEY = 'kurlyk.theme';

export function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'light';
    try {
      const saved = window.localStorage.getItem(THEME_KEY);
      if (saved === 'light' || saved === 'dark') return saved;
    } catch {
      // Приватный режим может запрещать localStorage — это не ошибка.
    }
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      window.localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Тема просто не запомнится.
    }
  }, [theme]);

  return [theme, setTheme];
}

/** Возвращает стабильный id для связывания подписей и полей. */
export function useIds<T extends string>(...names: T[]): Record<T, string> {
  const ref = useRef<Record<string, string> | null>(null);
  if (!ref.current) {
    const prefix = `k${Math.random().toString(36).slice(2, 8)}`;
    ref.current = Object.fromEntries(names.map((n) => [n, `${prefix}-${n}`]));
  }
  return ref.current as Record<T, string>;
}
